import { getAccessRequirementKnobValue, type AccessChecklistItem } from '@peanuds/app-shell';
import { UiKnobIcon, UserCardIdIcon } from '@peanuds/ui';

interface TotalBalanceOnboardingButton {
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger';
  label: string;
  disabled?: boolean;
}

interface TotalBalanceOnboardingIconProps {
  knobIcon: typeof UserCardIdIcon;
  showKnob: boolean;
  knobValue: number;
}

export interface TotalBalanceOnboardingPresentation {
  title: string;
  subtitle: string;
  button?: TotalBalanceOnboardingButton;
  iconComponent: typeof UiKnobIcon;
  iconProps: TotalBalanceOnboardingIconProps;
}

const isVerificationRejected = (currentItem: AccessChecklistItem) => {
  return currentItem.status === 'RED';
};

const resolveTitle = (currentItem: AccessChecklistItem) => {
  if (currentItem.key === 'email') {
    return 'Please confirm your email';
  }

  if (currentItem.key === 'phone') {
    return 'Phone Verification Required';
  }

  if (currentItem.status === 'YELLOW') {
    return 'Verification Under Review';
  }

  if (isVerificationRejected(currentItem) && currentItem.key === 'kyb') {
    return 'Business Application Unsuccessful';
  }

  if (isVerificationRejected(currentItem) && currentItem.key === 'kyc') {
    return 'Identity Verification Unsuccessful';
  }

  if (currentItem.status === 'RETRY') {
    return 'Verification Action Needed';
  }

  if (currentItem.key === 'kyb') {
    return 'Business Verification Required';
  }

  if (currentItem.key === 'kyc') {
    return 'Identity Verification Required';
  }

  return currentItem.stepLabel;
};

const resolveSubtitle = (currentItem: AccessChecklistItem) => {
  if (currentItem.key === 'email') {
    return 'Please confirm your email to continue onboarding and unlock account access.';
  }

  if (currentItem.key === 'phone') {
    return 'Please verify your phone number to continue onboarding and unlock account access.';
  }

  if (currentItem.status === 'YELLOW') {
    return 'We’re reviewing your information now and will notify you once the review is complete.';
  }

  if (isVerificationRejected(currentItem) && currentItem.key === 'kyb') {
    return 'We’re unable to open an account for your business.';
  }

  if (isVerificationRejected(currentItem) && currentItem.key === 'kyc') {
    return 'We’re unable to complete your identity verification.';
  }

  if (currentItem.status === 'RETRY') {
    return 'Please review the next steps and update your application.';
  }

  if (currentItem.key === 'kyb') {
    return 'Please complete your business verification to continue.';
  }

  if (currentItem.key === 'kyc') {
    return 'Please complete your identity verification to continue.';
  }

  if (currentItem.key === 'if') {
    return 'We’re finalizing your account now and will notify you once it is ready.';
  }

  return currentItem.statusLabel;
};

export const getTotalBalanceOnboardingPresentation = (
  currentItem: AccessChecklistItem,
): TotalBalanceOnboardingPresentation => {
  return {
    title: resolveTitle(currentItem),
    subtitle: resolveSubtitle(currentItem),
    iconComponent: UiKnobIcon,
    iconProps: {
      knobIcon: UserCardIdIcon,
      showKnob: true,
      knobValue: getAccessRequirementKnobValue(currentItem.key),
    },
    button: currentItem.ctaVisible
      ? {
          disabled: currentItem.ctaDisabled,
          label: currentItem.ctaLabel,
          severity: 'primary',
        }
      : undefined,
  };
};
